import { useCallback, useImperativeHandle, useRef, useState } from 'react'
import { ViewMode } from '@playbooks/action-cards-utils/src'

import { trackFunnelViewSlideChanged } from '../services/trackService'
import useMountEffect from './useMountEffect'
import { SliderManager, Step, StepType } from '../types'

const SWITCH_SLIDE_EVENT = 'WP-slide-switch'
const STEPS_CORRECTION = 1

function useHorizontalSliderMethods({
  viewMode,
  playbookId,
  sliderManagerRef,
  setIsFinishButtonVisible,
}: {
  viewMode: ViewMode | undefined
  playbookId: string
  sliderManagerRef: React.RefObject<SliderManager | null>
  setIsFinishButtonVisible: (flag: boolean) => void
}) {
  const [steps, setSteps] = useState<Step[]>([])
  const scrollContainer = useRef<HTMLDivElement>(null)
  const step = useRef(0)

  const mapScopesToSteps = useCallback(() => {
    if (viewMode !== ViewMode.FUNNEL) return
    if (!scrollContainer.current) return

    let stepCounter = 0
    // to ensure that slides are loaded and we can get the correct number of them
    const scopes = [...scrollContainer.current!.querySelectorAll('[data-separator-scope]')]

    const mappedSteps = scopes.map(x => {
      return {
        type: stepCounter === 0 ? StepType.FIRST : null,
        step: stepCounter++,
        stepsNumber: scopes.length,
        separatorId: Number.parseInt(x.getAttribute('data-separator-scope') || '0') || 0,
      }
    })

    mappedSteps.push({
      type: StepType.LAST,
      step: mappedSteps.length,
      stepsNumber: mappedSteps.length,
      separatorId: 0,
    })

    setSteps(mappedSteps)
    return mappedSteps
  }, [viewMode])

  useMountEffect(() => {
    const mappedSteps = mapScopesToSteps()
    if (!mappedSteps) return
    trackFunnelViewSlideChanged(playbookId, mappedSteps[0].separatorId)
  })

  useImperativeHandle(sliderManagerRef, () => {
    return {
      nextSlide: () => {
        triggerSlideSwitchEvent()
        const steps = mapScopesToSteps()
        if (!steps) return

        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (step.current + STEPS_CORRECTION),
          behavior: 'smooth',
        })

        if (steps[step.current].type !== StepType.LAST) step.current++

        const currentStepNumber = steps[step.current]

        trackFunnelViewSlideChanged(playbookId, currentStepNumber.separatorId)

        setIsFinishButtonVisible(steps[step.current].type === StepType.LAST)

        return currentStepNumber
      },
      prevSlide: () => {
        triggerSlideSwitchEvent()

        const stepSize = scrollContainer.current?.clientWidth || 0

        scrollContainer.current?.scrollTo({
          left: stepSize * (step.current - STEPS_CORRECTION),
          behavior: 'smooth',
        })

        if (steps[step.current].type !== StepType.FIRST) step.current--

        setIsFinishButtonVisible(steps[step.current].type === StepType.LAST)
        return steps[step.current]
      },
      getCurrentStep: () => {
        return steps[step.current]
      },
    }
  })

  return scrollContainer
}

function triggerSlideSwitchEvent() {
  /**
   * `dispatchEvent(new CustomEvent(SWITCH_SLIDE_EVENT))` is needed
   *  to message to the ac lib to stop video/audio playback in recommendations
   */
  dispatchEvent(new CustomEvent(SWITCH_SLIDE_EVENT))
}

export default useHorizontalSliderMethods
